/**
 * Event detail (FR-EVENTS-03/05/07, AC-EVENT-003/004, AC-PART-001).
 *
 * Layout: a one-row PageHeader (name, schedule, status pill, lifecycle actions),
 * then the event's reference details beside the work area — the participation
 * checklist — on wide screens (stacked on narrow ones). Edit opens EventForm in
 * a dialog.
 *   1. header — name, status pill (a closed event reads "Film Review Mode"),
 *      schedule; the film-review notice below it when closed;
 *   2. lifecycle + metadata — Start / Close / Reopen / Edit / Delete, gated by persona
 *      exactly the way `services/events` gates them, plus "Work on this event"
 *      which promotes the row into the session's active-event context;
 *   3. `ParticipationSection` — the roster checklist.
 *
 * Every transition that carries an audited reason goes through its dialog; the
 * one that does not (`pending → active`) is a plain PATCH with a toast.
 */
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { useActiveEvent } from '../../app/active-event-context';
import { useApiClient } from '../../app/providers';
import { useSession } from '../../app/session-context';
import { useTenantSlug } from '../../app/tenant-slug';
import { useToast } from '../../app/toast-context';
import { DeskSection } from '../../components/desk-section';
import { PageHeader } from '../../components/page-header';
import { StatusPill } from '../../components/status-pill';
import { ApiError } from '../../lib/api-error';
import type { TeamEvent } from '../../lib/api-types';
import { APP_VIEWS } from '../../lib/constants';
import {
  CLOSED_EVENT_TOOLTIP,
  EVENT_TYPE_LABELS,
  FILM_REVIEW_LABEL,
  formatEventDateTime,
} from '../../lib/event-format';

import { CloseEventDialog, DeleteEventDialog, ReopenEventDialog } from './close-reopen-dialogs';
import { EventStatusBadge } from './event-columns';
import { EventForm } from './event-form';
import { ParticipationSection } from './participation-section';
import {
  canPersona,
  EVENT_CLOSE_PERSONAS,
  EVENT_DELETE_PERSONAS,
  EVENT_REOPEN_PERSONAS,
  EVENT_WRITE_PERSONAS,
  useEventDetail,
} from './use-events';

type OpenDialog = 'none' | 'close' | 'reopen' | 'delete';

function MetadataList({ event }: { event: TeamEvent }): JSX.Element {
  const rows: readonly { label: string; value: string }[] = [
    { label: 'Type', value: EVENT_TYPE_LABELS[event.event_type] },
    { label: 'Season', value: String(event.season_year) },
    { label: 'Starts', value: formatEventDateTime(event.scheduled_at) },
    { label: 'Ends', value: formatEventDateTime(event.scheduled_end_at) },
    { label: 'Opponent', value: event.opponent_name ?? '—' },
    { label: 'Location', value: event.location ?? '—' },
    {
      label: 'Score',
      value:
        event.home_score === null && event.away_score === null
          ? '—'
          : `${event.home_score ?? 0} – ${event.away_score ?? 0}`,
    },
  ];
  return (
    <dl className="event-detail__meta">
      {rows.map((row) => (
        <div key={row.label}>
          <dt>{row.label}</dt>
          <dd>{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function EventDetailPage(): JSX.Element {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const tenantSlug = useTenantSlug();
  const apiClient = useApiClient();
  const { showToast } = useToast();
  const { persona } = useSession();
  const { activeEventId, setActiveEvent, refreshActiveEvent } = useActiveEvent();
  const detail = useEventDetail(eventId);
  const [dialog, setDialog] = useState<OpenDialog>('none');
  const [isEditing, setIsEditing] = useState(false);
  const [isStarting, setIsStarting] = useState(false);

  const event = detail.event;
  const listHref = `/app/${tenantSlug ?? ''}/${APP_VIEWS.events}`;

  function adopt(next: TeamEvent, message: string): void {
    detail.apply(next);
    refreshActiveEvent(next);
    setDialog('none');
    setIsEditing(false);
    showToast(message, 'success');
  }

  async function startEvent(current: TeamEvent): Promise<void> {
    setIsStarting(true);
    try {
      const next = await apiClient.updateEvent(current.event_id, { status: 'active' });
      adopt(next, `${next.name} is now active.`);
    } catch (caught) {
      showToast(
        ApiError.isApiError(caught)
          ? caught.message
          : 'The event could not be started. Please try again.',
        'error'
      );
    } finally {
      setIsStarting(false);
    }
  }

  if (detail.isLoading) {
    return (
      <section className="stack">
        <p role="status">Loading the event…</p>
      </section>
    );
  }

  if (event === null) {
    return (
      <section className="stack">
        <div className="banner banner--error" role="alert">
          <p className="banner__title">Event unavailable</p>
          <p>{detail.error ?? 'This event could not be found.'}</p>
        </div>
        <Link className="btn btn--secondary" to={listHref}>
          Back to Event Manager
        </Link>
      </section>
    );
  }

  const isClosed = event.status === 'closed';
  const canEditClosed = canPersona(persona, EVENT_REOPEN_PERSONAS);
  const canEdit = canPersona(persona, EVENT_WRITE_PERSONAS) && (!isClosed || canEditClosed);
  const canClose = canPersona(persona, EVENT_CLOSE_PERSONAS) && event.status === 'active';
  const canReopen = canEditClosed && isClosed;
  const canDelete = canPersona(persona, EVENT_DELETE_PERSONAS);
  const isActiveContext = activeEventId === event.event_id;

  return (
    <section className="stack event-detail">
      <nav className="event-detail__crumbs" aria-label="Breadcrumb">
        <Link to={listHref}>Events</Link> <span aria-hidden="true">/</span>{' '}
        <span>{event.name}</span>
      </nav>

      <PageHeader
        className="event-detail__header"
        title={event.name}
        context={
          <>
            {EVENT_TYPE_LABELS[event.event_type]} ·{' '}
            {event.opponent_name === null ? 'No opponent' : `vs. ${event.opponent_name}`} ·{' '}
            {formatEventDateTime(event.scheduled_at)}
          </>
        }
        meta={
          <span className="event-detail__status">
            <EventStatusBadge event={event} />
            {isActiveContext ? <StatusPill tone="brand">Current event</StatusPill> : null}
          </span>
        }
        actions={
          <>
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              disabled={isActiveContext}
              onClick={() => {
                setActiveEvent(event);
                showToast(`Now working on ${event.name}.`, 'success');
              }}
            >
              {isActiveContext ? 'Working on this' : 'Work on this event'}
            </button>
            {canEdit ? (
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                onClick={() => setIsEditing(true)}
              >
                Edit event
              </button>
            ) : null}
            {canDelete ? (
              <button
                type="button"
                className="btn btn--danger btn--sm"
                onClick={() => setDialog('delete')}
              >
                Delete Event
              </button>
            ) : null}
            {canEdit && event.status === 'pending' ? (
              <button
                type="button"
                className="btn btn--primary btn--sm"
                disabled={isStarting}
                onClick={() => void startEvent(event)}
              >
                {isStarting ? 'Starting…' : 'Start Event'}
              </button>
            ) : null}
            {canClose ? (
              <button
                type="button"
                className="btn btn--primary btn--sm"
                onClick={() => setDialog('close')}
              >
                Close Event
              </button>
            ) : null}
            {canReopen ? (
              <button
                type="button"
                className="btn btn--primary btn--sm"
                onClick={() => setDialog('reopen')}
              >
                Reopen Event
              </button>
            ) : null}
          </>
        }
      />

      {isClosed ? (
        <div className="banner banner--warning event-detail__notice" role="status">
          <p>
            <strong>{FILM_REVIEW_LABEL}</strong> · {CLOSED_EVENT_TOOLTIP}
            {event.closed_reason !== undefined && event.closed_reason !== null ? (
              <>
                {' '}
                Closed {formatEventDateTime(event.closed_at)} —{' '}
                {event.is_auto_closed === true
                  ? 'automatically, after the configured timeout.'
                  : `reason: ${event.closed_reason}`}
              </>
            ) : null}
          </p>
        </div>
      ) : null}

      {detail.error !== null ? (
        <div className="banner banner--error" role="alert">
          <p>{detail.error}</p>
        </div>
      ) : null}

      <div className="event-detail__layout">
        <DeskSection title="Event details" className="event-detail__panel">
          <MetadataList event={event} />
        </DeskSection>

        <ParticipationSection event={event} persona={persona} />
      </div>

      {isEditing ? (
        <EventForm
          event={event}
          onSave={(input) => apiClient.updateEvent(event.event_id, input)}
          onSaved={(saved) => adopt(saved, `${saved.name} updated.`)}
          onCancel={() => setIsEditing(false)}
        />
      ) : null}

      {dialog === 'close' ? (
        <CloseEventDialog
          event={event}
          onCancel={() => setDialog('none')}
          onDone={(next) => adopt(next, `${next.name} is closed. Film-review mode is on.`)}
        />
      ) : null}
      {dialog === 'reopen' ? (
        <ReopenEventDialog
          event={event}
          onCancel={() => setDialog('none')}
          onDone={(next) => adopt(next, `${next.name} is reopened. Staff can edit plays again.`)}
        />
      ) : null}
      {dialog === 'delete' ? (
        <DeleteEventDialog
          event={event}
          onCancel={() => setDialog('none')}
          onDeleted={(deleted) => {
            if (deleted.event_id === activeEventId) setActiveEvent(null);
            setDialog('none');
            showToast('Event deleted', 'success');
            navigate(listHref, { replace: true });
          }}
        />
      ) : null}
    </section>
  );
}
